import { useEffect, useState, useRef } from "react";
import { Eye, EyeOff } from 'lucide-react';
import { auth, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, onAuthStateChanged, sendEmailVerification, sendPasswordResetEmail } from "./firebase";
import { createProTrial } from "@/services/trialService";

const rutappLogo = "/images/rutapp-logo.png.png";

// 🔓 BYPASS DE LOGIN TEMPORAL - Para desarrollo
// Para REACTIVAR el login: Cambiar BYPASS_LOGIN_TEMPORARILY a false (línea 9)
const BYPASS_LOGIN_TEMPORARILY = false;

// Usuario mock para bypass - reemplazar cuando se reactive el login
const MOCK_USER = {
  uid: 'dev-mock-user-12345',
  email: 'test@development.local',
  emailVerified: false
};

export default function AuthGate({ children }) {
  const [user, setUser] = useState(BYPASS_LOGIN_TEMPORARILY ? MOCK_USER : null);
  const [loading, setLoading] = useState(!BYPASS_LOGIN_TEMPORARILY);
  const [showForm, setShowForm] = useState(false);
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
   const [error, setError] = useState("");
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [minSplashTimeElapsed, setMinSplashTimeElapsed] = useState(false);
   const [forgotPassword, setForgotPassword] = useState(false);
   const [verificationSent, setVerificationSent] = useState(false);
   const [submittedEmail, setSubmittedEmail] = useState("");
   const [emailNotVerified, setEmailNotVerified] = useState(false);
   const [successMessage, setSuccessMessage] = useState("");
   const [showPass, setShowPass] = useState(false);
   const autoLoginAttemptedRef = useRef(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setMinSplashTimeElapsed(true);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    console.log("[AUTH_GATE] mounted");
    
    // 🔓 Si BYPASS_LOGIN_TEMPORARILY está activo, no hacer nada con Firebase Auth
    if (BYPASS_LOGIN_TEMPORARILY) {
      console.log("[AUTH_GATE] BYPASS MODE ACTIVE - Saltando Firebase Auth");
      return () => {};
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log("[AUTH_GATE] onAuthStateChanged triggered", { user: !!user });
      
      // SIEMPRE actualizar estado: user puede ser null o un objeto usuario
      setUser(user);
      setLoading(false);
      
      if (user) {
        console.log(`✅ Usuario conectado: ${user.uid}`);
      } else {
        console.log("❌ Usuario desconectado");
        
        // 🔐 AUTO-LOGIN EN MODO DESARROLLO
        if (
          import.meta.env.DEV &&
          import.meta.env.VITE_DEV_AUTO_LOGIN === "true" &&
          !autoLoginAttemptedRef.current
        ) {
          autoLoginAttemptedRef.current = true;
          const devEmail = import.meta.env.VITE_DEV_AUTO_LOGIN_EMAIL;
          const devPassword = import.meta.env.VITE_DEV_AUTO_LOGIN_PASSWORD;
          
          if (devEmail && devPassword) {
            console.log("[DEV_AUTO_LOGIN] attempting...");
            try {
              await signInWithEmailAndPassword(auth, devEmail, devPassword);
              console.log("[DEV_AUTO_LOGIN] success");
              // onAuthStateChanged se disparará automáticamente con el nuevo usuario
            } catch (err) {
              console.warn("[DEV_AUTO_LOGIN] failed:", err.message);
              // No romper nada, dejar que el usuario vea el formulario de login
            }
          }
        }
      }
    });

    return () => {
      console.log("[AUTH_GATE] cleanup");
      unsubscribe();
    };
  }, []);

   async function handleSubmit(e) {
     e.preventDefault();
     setError("");
     setSuccessMessage("");
     setEmailNotVerified(false);
     setIsSubmitting(true);

     try {
         if (isRegister) {
          console.log("🚀 Registrando nuevo usuario...");
          const cred = await createUserWithEmailAndPassword(auth, email.trim(), pass);
          console.log("✅ Usuario registrado exitosamente");

          // 🎁 Crear trial Pro 60 días (non-blocking — si falla no rompe el registro)
          await createProTrial(cred.user.uid);

          // 📧 Enviar email de verificación
          console.log("📧 Enviando email de verificación...");
          await sendEmailVerification(cred.user);
          console.log("✅ Email de verificación enviado");
         
         // 🔓 Desconectar hasta que verifique
         await signOut(auth);
         console.log("🔓 Usuario desconectado hasta verificar email");
         
         setSubmittedEmail(email.trim());
         setVerificationSent(true);
         setShowForm(false);
         setEmail("");
         setPass("");
       } else {
         console.log("🚀 Iniciando sesión...");
         const cred = await signInWithEmailAndPassword(auth, email.trim(), pass);
         console.log("✅ Sesión iniciada exitosamente");
         
         // 🔄 Recargar estado del usuario desde Firebase
         await cred.user.reload();
         
         if (!cred.user.emailVerified) {
           console.warn("⚠️ Email no verificado");
           await signOut(auth);
           setEmailNotVerified(true);
           setError("");
           return;
         }
         
         setShowForm(false);
         setEmail("");
         setPass("");
       }
     } catch (err) {
       console.error("❌ Error de autenticación:", err.message);
       setError(getErrorMessage(err.code));
     } finally {
       setIsSubmitting(false);
     }
   }

   async function handleLogout() {
     console.log("🚀 Cerrando sesión...");
     await signOut(auth);
     console.log("✅ Sesión cerrada");
   }

   async function handleForgotPassword(e) {
     e.preventDefault();
     setError("");
     setIsSubmitting(true);

     try {
       if (!email.trim()) {
         setError("Por favor introduce tu email");
         setIsSubmitting(false);
         return;
       }
       console.log("📧 Enviando email de recuperación de contraseña...");
       await sendPasswordResetEmail(auth, email.trim());
        console.log("✅ Email de recuperación enviado");
        setError("");
        setForgotPassword(false);
        setShowForm(false);
        setEmail("");
        setPass("");
        // Mostrar confirmación brevemente
        setSuccessMessage("Revisa tu email para recuperar tu contraseña");
     } catch (err) {
       console.error("❌ Error al enviar email de recuperación:", err.message);
       setError(getErrorMessage(err.code));
     } finally {
       setIsSubmitting(false);
     }
   }

   async function handleResendVerificationEmail(e) {
     e.preventDefault();
     setError("");
     setIsSubmitting(true);

     try {
       if (!email.trim()) {
         setError("Por favor introduce tu email");
         setIsSubmitting(false);
         return;
       }
       console.log("📧 Reenviando email de verificación...");
       // Intentar iniciar sesión para obtener el usuario
       const cred = await signInWithEmailAndPassword(auth, email.trim(), pass);
       await sendEmailVerification(cred.user);
       await signOut(auth);
       console.log("✅ Email de verificación reenviado");
       setError("");
       setEmailNotVerified(false);
       setEmail("");
       setPass("");
     } catch (err) {
       console.error("❌ Error al reenviar email de verificación:", err.message);
       setError(getErrorMessage(err.code));
     } finally {
       setIsSubmitting(false);
     }
   }

  function getErrorMessage(errorCode) {
    switch (errorCode) {
      case 'auth/user-not-found': return 'Usuario no encontrado';
      case 'auth/wrong-password': return 'Contraseña incorrecta';
      case 'auth/email-already-in-use': return 'El email ya está registrado';
      case 'auth/weak-password': return 'La contraseña debe tener al menos 6 caracteres';
      case 'auth/invalid-email': return 'Email inválido';
      default: return 'Error de autenticación';
    }
  }

   if (!minSplashTimeElapsed) {
     console.log("[AUTH_GATE] render splash");
     return (
       <div className="w-full h-screen flex flex-col items-center justify-center gap-4 bg-black">
        <img
          src={rutappLogo}
          alt="Rutapp"
          className="h-28 w-auto opacity-90"
        />
        <p style={{color: '#B0B0B0'}}>Cargando…</p>
      </div>
    );
  }

  if (!user) {
    console.log("[AUTH_GATE] render login");
  }

  if (user) {
    console.log("[AUTH_GATE] render app");
  }

  return (
    <>
      {user && (
        <div style={{padding: '16px'}}>
          {children}
        </div>
      )}

      {!user && (
        <>
          {error && (
            <div style={{
              padding: '12px 16px',
              background: '#7f1d1d',
              borderLeft: '4px solid #dc2626',
              color: '#fecaca'
            }}>
              ❌ {error}
            </div>
          )}

          {successMessage && (
            <div style={{
              padding: '12px 16px',
              background: '#14532d',
              borderLeft: '4px solid #16a34a',
              color: '#bbf7d0'
            }}>
              ✅ {successMessage}
              <p style={{margin: '6px 0 0 0', fontSize: '12px', color: '#86efac'}}>
                Si no lo ves en unos minutos, revisa también la carpeta de spam.
              </p>
            </div>
          )}

          {loading ? (
            <div className="flex justify-center items-center h-screen flex-col gap-4 bg-[#121212]" style={{
              color: '#FFFFFF'
            }}>
              <div style={{fontSize: '24px'}}>🔄</div>
              <div>Verificando autenticación...</div>
            </div>
          ) : (
            <div className="bg-[#121212] min-h-screen" style={{
              padding: '64px 16px',
              textAlign: 'center',
              color: '#EAEAEA'
            }}>
              <img
                src={rutappLogo}
                alt="Rutapp"
                style={{ height: '80px', width: 'auto', marginBottom: '24px', display: 'block', margin: '0 auto 24px auto' }}
              />

              {/* PANTALLA: Verificación enviada */}
              {verificationSent && (
                <>
                  <div style={{fontSize: '48px', marginBottom: '16px'}}>📧</div>
                  <h2 style={{marginBottom: '12px'}}>Verifica tu email</h2>
                  <p style={{marginBottom: '24px'}}>Hemos enviado un enlace de verificación a:<br /><strong>{submittedEmail}</strong></p>
                  <p style={{marginBottom: '24px', fontSize: '14px', color: '#B0B0B0'}}>Haz clic en el enlace del email para activar tu cuenta. Luego podrás iniciar sesión.</p>
                  <p style={{fontSize: '12px', color: '#808080'}}>Si no lo ves en unos minutos, revisa también la carpeta de spam.</p>
                  <button
                    onClick={() => {
                      setVerificationSent(false);
                      setIsRegister(false);
                      setShowForm(true);
                    }}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '6px',
                      border: '1px solid #3b82f6',
                      background: '#3b82f6',
                      color: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    Volver a Iniciar Sesión
                  </button>
                </>
              )}

              {/* PANTALLA: Email no verificado (usuario antiguo) */}
              {emailNotVerified && (
                <>
                  <div style={{fontSize: '48px', marginBottom: '16px'}}>⚠️</div>
                  <h2 style={{marginBottom: '12px'}}>Email no verificado</h2>
                  <p style={{marginBottom: '24px'}}>Por favor verifica tu email antes de continuar.</p>
                  <button
                    onClick={(e) => handleResendVerificationEmail(e)}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '6px',
                      border: '1px solid #059669',
                      background: '#059669',
                      color: 'white',
                      cursor: 'pointer',
                      marginRight: '8px'
                    }}
                  >
                    Reenviar verificación
                  </button>
                  <button
                    onClick={() => setEmailNotVerified(false)}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '6px',
                      border: '1px solid #4B5563',
                      background: 'transparent',
                      color: '#B0B0B0',
                      cursor: 'pointer'
                    }}
                  >
                    Volver
                  </button>
                </>
              )}

              {/* PANTALLA: Formulario normal (login/registro o recuperar contraseña) */}
              {!verificationSent && !emailNotVerified && (
                <>
                  <div style={{fontSize: '48px', marginBottom: '16px'}}>🔒</div>
                  <h2 style={{marginBottom: '8px'}}>Acceso Restringido</h2>
                  <p>Debes iniciar sesión para acceder a la aplicación.</p>
                  
                  <div style={{marginTop: '24px'}}>
                    {!forgotPassword && (
                      <>
                        <button 
                          onClick={() => { setShowForm(v => !v); setSuccessMessage(""); }}
                          style={{
                            padding: '8px 16px',
                            borderRadius: '6px',
                            border: '1px solid #3b82f6',
                            background: '#3b82f6',
                            color: 'white',
                            cursor: 'pointer',
                            marginBottom: '16px'
                          }}
                        >
                          {showForm ? 'Cancelar' : 'Iniciar Sesión'}
                        </button>
                        
                        {showForm && (
                          <div style={{
                            display: 'flex',
                            gap: '12px',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexWrap: 'wrap'
                          }}>
                            <form onSubmit={handleSubmit} style={{
                              display: 'flex',
                              gap: '8px',
                              alignItems: 'center',
                              flexWrap: 'wrap',
                              justifyContent: 'center'
                            }}>
                              <input 
                                value={email} 
                                onChange={e => setEmail(e.target.value)} 
                                type="email" 
                                placeholder="email@ejemplo.com" 
                                required
                                style={{
                                  padding: '8px 12px',
                                  borderRadius: '4px',
                                  border: '1px solid #2A2A2A',
                                  minWidth: '200px',
                                  backgroundColor: '#1E1E1E',
                                  color: '#FFFFFF'
                                }}
                              />
                              <div style={{ position: 'relative', display: 'inline-block' }}>
                                <input 
                                  value={pass} 
                                  onChange={e => setPass(e.target.value)} 
                                  type={showPass ? "text" : "password"} 
                                  placeholder="contraseña" 
                                  required
                                  style={{
                                    padding: '8px 12px',
                                    paddingRight: '36px',
                                    borderRadius: '4px',
                                    border: '1px solid #2A2A2A',
                                    minWidth: '150px',
                                    backgroundColor: '#1E1E1E',
                                    color: '#FFFFFF'
                                  }}
                                />
                                <button
                                  type="button"
                                  onClick={() => setShowPass(v => !v)}
                                  style={{
                                    position: 'absolute',
                                    right: '8px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: '#B0B0B0',
                                    padding: '0',
                                    display: 'flex',
                                    alignItems: 'center'
                                  }}
                                >
                                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                              </div>
                              <button 
                                type="submit" 
                                disabled={isSubmitting}
                                style={{
                                  padding: '8px 16px',
                                  borderRadius: '4px',
                                  border: 'none',
                                  background: isRegister ? '#059669' : '#3b82f6',
                                  color: 'white',
                                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                  opacity: isSubmitting ? 0.7 : 1
                                }}
                              >
                                {isSubmitting ? '...' : (isRegister ? 'Registrarse' : 'Entrar')}
                              </button>
                            </form>
                            
                            <button
                              type="button"
                              onClick={() => { setIsRegister(v => !v); setSuccessMessage(""); }}
                              style={{
                                padding: '6px 12px',
                                borderRadius: '4px',
                                border: '1px solid #4B5563',
                                background: 'transparent',
                                color: '#B0B0B0',
                                cursor: 'pointer',
                                fontSize: '14px'
                              }}
                            >
                              {isRegister ? '¿Ya tienes cuenta?' : '¿Nuevo usuario?'}
                            </button>

                            {!isRegister && (
                              <button
                                type="button"
                                onClick={() => {
                                  setForgotPassword(true);
                                  setShowForm(false);
                                  setEmail("");
                                  setPass("");
                                }}
                                style={{
                                  padding: '6px 12px',
                                  borderRadius: '4px',
                                  border: '1px solid #4B5563',
                                  background: 'transparent',
                                  color: '#B0B0B0',
                                  cursor: 'pointer',
                                  fontSize: '12px'
                                }}
                              >
                                ¿Olvidaste tu contraseña?
                              </button>
                            )}
                          </div>
                        )}
                      </>
                    )}

                    {forgotPassword && (
                      <div style={{marginTop: '24px'}}>
                        <h3 style={{marginBottom: '16px'}}>Recuperar contraseña</h3>
                        <form onSubmit={handleForgotPassword} style={{
                          display: 'flex',
                          gap: '8px',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          justifyContent: 'center'
                        }}>
                          <input 
                            value={email} 
                            onChange={e => setEmail(e.target.value)} 
                            type="email" 
                            placeholder="email@ejemplo.com" 
                            required
                            style={{
                              padding: '8px 12px',
                              borderRadius: '4px',
                              border: '1px solid #2A2A2A',
                              minWidth: '200px',
                              backgroundColor: '#1E1E1E',
                              color: '#FFFFFF'
                            }}
                          />
                          <button 
                            type="submit" 
                            disabled={isSubmitting}
                            style={{
                              padding: '8px 16px',
                              borderRadius: '4px',
                              border: 'none',
                              background: '#d97706',
                              color: 'white',
                              cursor: isSubmitting ? 'not-allowed' : 'pointer',
                              opacity: isSubmitting ? 0.7 : 1
                            }}
                          >
                            {isSubmitting ? '...' : 'Enviar'}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setForgotPassword(false);
                              setShowForm(true);
                              setEmail("");
                              setPass("");
                              setSuccessMessage("");
                            }}
                            style={{
                              padding: '6px 12px',
                              borderRadius: '4px',
                              border: '1px solid #4B5563',
                              background: 'transparent',
                              color: '#B0B0B0',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            Volver
                          </button>
                        </form>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}
    </>
  );
}
